import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const handleCategoryClick = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wider mb-4">
            Product Categories
          </span>
          <h2 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-3">
            Browse by Category
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
            Explore our organized product categories and find exactly what you are looking for on the main platform.
          </p>
        </div>

        <div className="flex justify-center mb-10">
          <div className="relative w-full max-w-sm" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between gap-3 px-5 py-3.5 rounded-xl bg-background-100 border-2 border-background-200 text-foreground-700 text-sm font-medium hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-grid-line text-lg"></i>
                </div>
                <span>Select a Category</span>
              </div>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </div>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-full bg-background-50 border border-background-200/70 rounded-xl shadow-lg py-2 max-h-72 overflow-y-auto z-50">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => {
                      setDropdownOpen(false);
                      handleCategoryClick(cat.url);
                    }}
                    className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap text-left"
                  >
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className={`${cat.icon} text-base`}></i>
                    </div>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => handleCategoryClick(cat.url)}
              className="group flex flex-col items-center gap-2.5 p-4 md:p-5 rounded-xl bg-background-100 border border-background-200/60 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer hover:-translate-y-1"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-accent-100 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                <i className={`${cat.icon} text-lg md:text-xl text-accent-600 group-hover:text-primary-600 transition-colors`}></i>
              </div>
              <span className="text-xs md:text-sm font-medium text-foreground-800 group-hover:text-primary-700 transition-colors text-center leading-tight whitespace-nowrap">
                {cat.name}
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}